import { useContext } from 'react';

import { type IServerSideDatasource } from 'ag-grid-community';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { useNavigate } from 'react-router-dom';

import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { PrimaryContext } from '@/services';

import type { AuditFeedItem } from '../api';
import { auditFeedColumnDefs } from './columns';

const PAGE_SIZE = 50;
const LOAD_DEBOUNCE_MS = 250;

function NoActivity() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2">
      <div className="text-2xl font-semibold text-gray-700 dark:text-gray-300">
        No Audit Activity Found
      </div>
      <div className="text-center text-gray-500 dark:text-gray-400">
        Try a different entity, search or date range.
      </div>
    </div>
  );
}

type Props = {
  grid: React.RefObject<AgGridReact>;
  dataSource: IServerSideDatasource;
};

export function AuditFeedGrid({ grid, dataSource }: Props) {
  const { themeMode } = useContext(PrimaryContext);
  const defaultGridOptions = useDefaultGridOptions();
  const navigate = useNavigate();

  return (
    <AgGridReact
      gridOptions={defaultGridOptions}
      ref={grid}
      className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full`}
      columnDefs={auditFeedColumnDefs as AgGridReactProps['columnDefs']}
      defaultColDef={defaultColDef}
      rowModelType="serverSide"
      serverSideDatasource={dataSource}
      cacheBlockSize={PAGE_SIZE}
      blockLoadDebounceMillis={LOAD_DEBOUNCE_MS}
      pagination
      paginationPageSize={PAGE_SIZE}
      getRowId={getRowId<AuditFeedItem>((row) => row.data.id)}
      tooltipShowDelay={300}
      rowClass="cursor-pointer"
      onRowClicked={(params) => {
        const row = params.data as AuditFeedItem | undefined;
        if (!row) return;
        const path = `/audit/${row.objectId}`;
        const event = params.event as MouseEvent | null | undefined;
        if (event?.metaKey || event?.ctrlKey) window.open(path, '_blank');
        else navigate(path);
      }}
      noRowsOverlayComponent={NoActivity}
    />
  );
}
